import { fetchAuthSession } from "aws-amplify/auth";
import { useAuthStore } from "../stores/auth.js";
import logger from "./logger.js";
import {
  TOKEN_FALLBACK_MS,
  isAuthError,
  redirectToLogin,
} from "./authSession.js";

class TokenService {
  constructor() {
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  async getTokens() {
    try {
      const authStore = useAuthStore();
      const session = await fetchAuthSession({ forceRefresh: false });
      const idToken = session.tokens?.idToken?.toString();
      const refreshToken = session.tokens?.accessToken?.toString();
      const expiresAt = session.tokens?.accessToken?.payload?.exp
        ? session.tokens.accessToken.payload.exp * 1000
        : Date.now() + TOKEN_FALLBACK_MS;

      authStore.setTokens({
        accessToken: idToken,
        refreshToken,
        expiry: new Date(expiresAt).toISOString(),
      });

      return {
        accessToken: idToken,
        refreshToken,
        expiresAt,
      };
    } catch (error) {
      console.error("Error obteniendo tokens:", error);
      if (isAuthError(error)) await redirectToLogin();
      throw error;
    }
  }

  async refreshAccessToken() {
    if (this.isRefreshing) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this._performRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  async _performRefresh() {
    try {
      const authStore = useAuthStore();
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();
      const expiresAt = session.tokens?.accessToken?.payload?.exp
        ? session.tokens.accessToken.payload.exp * 1000
        : Date.now() + TOKEN_FALLBACK_MS;

      authStore.setTokens({
        accessToken: idToken,
        refreshToken: authStore.refreshToken,
        expiry: new Date(expiresAt).toISOString(),
      });

      return { accessToken: idToken, expiresAt };
    } catch (error) {
      console.error("Error refrescando token:", error);
      this.clearTokens();
      await redirectToLogin();
      throw error;
    }
  }

  async getValidToken() {
    const authStore = useAuthStore();

    if (!authStore.isTokenValid) {
      try {
        await this.getTokens();
      } catch (error) {
        try {
          await this.refreshAccessToken();
        } catch (refreshError) {
          this.clearTokens();
          await redirectToLogin();
          throw new Error("No se pudo obtener un token valido");
        }
      }
    }

    return authStore.accessToken;
  }

  async authenticatedFetch(url, options = {}) {
    try {
      const token = await this.getValidToken();

      const authOptions = {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };
      let response = await fetch(url, authOptions);

      if (response.status === 401) {
        try {
          await this.refreshAccessToken();
          const newToken = await this.getValidToken();
          authOptions.headers.Authorization = `Bearer ${newToken}`;
          response = await fetch(url, authOptions);
        } catch {
          this.clearTokens();
          await redirectToLogin();
          throw new Error("Sesion expirada");
        }
      }

      if (response.status === 401 || response.status === 403) {
        this.clearTokens();
        await redirectToLogin();
        throw new Error("Sesion expirada");
      }

      return response;
    } catch (error) {
      if (isAuthError(error)) await redirectToLogin();
      if (error.name === "TypeError" && error.message === "Failed to fetch") {
        console.error("ERROR DE RED: Failed to fetch");
      }
      throw error;
    }
  }

  buildUrl(endpoint) {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    const cleanEndpoint = endpoint.startsWith("/")
      ? endpoint.slice(1)
      : endpoint;
    return `${baseUrl}/${cleanEndpoint}`;
  }

  async get(endpoint) {
    const url = this.buildUrl(endpoint);
    logger.apiCall("GET", url);
    const response = await this.authenticatedFetch(url, { method: "GET" });
    const data = await response.json();
    logger.apiResponse(data, endpoint);
    return data;
  }

  async post(endpoint, data) {
    const url = this.buildUrl(endpoint);
    logger.apiCall("POST", url, data);
    const response = await this.authenticatedFetch(url, {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (response.status === 204 || response.status === 200) {
      const text = await response.text();
      if (text) {
        try {
          const jsonData = JSON.parse(text);
          logger.apiResponse(jsonData, endpoint);
          return jsonData;
        } catch {
          logger.apiResponse(text, endpoint);
          return { success: true, message: text };
        }
      } else {
        logger.apiResponse("Respuesta vacia", endpoint);
        return { success: true, message: "Operacion exitosa" };
      }
    }

    try {
      const jsonData = await response.json();
      logger.apiResponse(jsonData, endpoint);
      return jsonData;
    } catch (error) {
      logger.error(`Error parseando respuesta JSON para ${endpoint}:`, error);
      return { success: false, error: "Error parseando respuesta" };
    }
  }

  async put(endpoint, data) {
    const url = this.buildUrl(endpoint);
    return await this.authenticatedFetch(url, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    const url = this.buildUrl(endpoint);
    const response = await this.authenticatedFetch(url, {
      method: "DELETE",
      headers: {},
    });
    return response;
  }

  clearTokens() {
    const authStore = useAuthStore();
    authStore.setTokens({
      accessToken: null,
      refreshToken: null,
      expiry: null,
    });
  }

  initialize() {
    // Los tokens se cargan automaticamente desde localStorage
    // gracias a Pinia persist plugin
  }
}

export default new TokenService();
